import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { UserPlus, Phone, Mail, Lock, Users, CheckCircle, RefreshCw, Rocket, Star, TrendingUp, Award, ArrowRight, Info } from 'lucide-react';
import { useOTP } from '@hooks/useOTP';
import { ROUTES, VALIDATION_MESSAGES, STORAGE_KEYS, USER_ROLES } from '@constants';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import PasswordInput from '@components/common/PasswordInput';
import PhoneInput from '@components/common/PhoneInput';
import FowBrand from '@components/common/FowBrand';
import { localStorage } from '@utils/storage';
import apiService from '@services/api/api.service';
import { logger } from '@utils/logger';

const registerSchema = z.object({
  phone: z.string()
    .min(10, VALIDATION_MESSAGES.PHONE_INVALID)
    .max(15, VALIDATION_MESSAGES.PHONE_INVALID)
    .regex(/^[0-9]+$/, 'Phone number must contain only digits'),
  phoneOtp: z.string().length(6, 'OTP must be 6 digits').optional(),
  firstName: z.string().min(2, 'First name must be at least 2 characters').max(50, 'First name is too long'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters').max(50, 'Last name is too long'),
  email: z.string().email(VALIDATION_MESSAGES.EMAIL_INVALID),
  emailOtp: z.string().length(6, 'OTP must be 6 digits').optional(),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  role: z.enum([USER_ROLES.STUDENT, USER_ROLES.MENTOR], {
    required_error: 'Please select a role',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: VALIDATION_MESSAGES.PASSWORD_MISMATCH,
  path: ['confirmPassword'],
});

const STEPS = [
  { id: 1, label: 'Phone', icon: Phone },
  { id: 2, label: 'Email', icon: Mail },
  { id: 3, label: 'Details', icon: Lock },
];

const FEATURES = [
  { icon: Rocket,     title: 'Launch Your Career',   desc: 'Curated roadmaps and resources tailored to your goals.' },
  { icon: TrendingUp, title: 'Track Your Growth',     desc: 'Visualise progress with real-time insights.' },
  { icon: Award,      title: 'Learn from Experts',    desc: 'Get mentored by industry professionals.' },
  { icon: Star,       title: 'Join 50,000+ Members',  desc: 'A thriving community of learners and mentors.' },
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { sendPhoneOTP, verifyPhoneOTP, sendEmailOTP, verifyEmailOTP, loading } = useOTP();

  const [currentStep, setCurrentStep]     = useState(1);
  const [userId, setUserId]               = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent]   = useState(false);
  const [emailOtpSent, setEmailOtpSent]   = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [submitting, setSubmitting]       = useState(false);

  const { register, handleSubmit, watch, formState: { errors }, trigger, getValues } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: { role: USER_ROLES.STUDENT },
  });

  const phoneValue = watch('phone');
  const emailValue = watch('email');
  const passwordValue = watch('password');

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendPhoneOTP = async () => {
    const isValid = await trigger('phone');
    if (!isValid) return;
    try {
      const result = await sendPhoneOTP(phoneValue);
      console.log('Send Phone OTP Response:', result);
      // API service wraps response: result.data contains the actual backend response
      const responseData = result.data?.data || result.data;
      if (result.success && responseData && responseData.userId) {
        console.log('Setting userId:', responseData.userId);
        setUserId(responseData.userId);
        setPhoneOtpSent(true);
        setResendCooldown(60);
        localStorage.set(STORAGE_KEYS.PENDING_USER_ID, responseData.userId);
        toast.success('OTP sent to your phone');
      } else {
        console.error('Invalid response structure:', result);
        toast.error('Failed to get user ID from server');
      }
    } catch (error) { 
      console.error('Send Phone OTP Error:', error);
      toast.error(error.message || 'Failed to send OTP'); 
    }
  };

  const handleVerifyPhoneOTP = async () => {
    const otp = getValues('phoneOtp');
    if (!otp || otp.length !== 6) { toast.error('Please enter 6-digit OTP'); return; }
    console.log('Verifying Phone OTP with userId:', userId, 'otp:', otp);
    if (!userId) {
      toast.error('User ID is missing. Please resend OTP.');
      return;
    }
    try {
      const result = await verifyPhoneOTP(userId, otp);
      console.log('Verify Phone OTP Response:', result);
      if (result.success) { setPhoneVerified(true); setCurrentStep(2); toast.success('Phone verified successfully!'); }
    } catch (error) { 
      console.error('Verify Phone OTP Error:', error);
      toast.error(error.message || 'Invalid OTP'); 
    }
  };

  const handleSendEmailOTP = async () => {
    const isValid = await trigger(['firstName', 'lastName', 'email']);
    if (!isValid) return;
    try {
      const firstName = getValues('firstName');
      const lastName = getValues('lastName');
      const result = await sendEmailOTP(userId, emailValue, firstName, lastName);
      if (result.success) { setEmailOtpSent(true); setResendCooldown(60); toast.success('OTP sent to your email'); }
    } catch (error) { toast.error(error.message || 'Failed to send OTP'); }
  };

  const handleVerifyEmailOTP = async () => {
    const otp = getValues('emailOtp');
    if (!otp || otp.length !== 6) { toast.error('Please enter 6-digit OTP'); return; }
    try {
      const result = await verifyEmailOTP(userId, otp);
      if (result.success) { setEmailVerified(true); setCurrentStep(3); toast.success('Email verified successfully!'); }
    } catch (error) { toast.error(error.message || 'Invalid OTP'); }
  };

  const handleResendOTP = async (type) => {
    if (resendCooldown > 0) { toast.error(`Please wait ${resendCooldown} seconds`); return; }
    try {
      if (type === 'phone') await handleSendPhoneOTP();
      else await handleSendEmailOTP();
    } catch (error) { toast.error('Failed to resend OTP'); }
  };

  const onSubmit = async (data) => {
    if (!phoneVerified || !emailVerified) { toast.error('Please verify both phone and email'); return; }
    try {
      setSubmitting(true);
      const response = await apiService.post('/auth/complete-registration', { userId, password: data.password, role: data.role });
      if (response.success) {
        toast.success('Registration completed successfully!');
        localStorage.remove(STORAGE_KEYS.PENDING_USER_ID);
        setTimeout(() => navigate(ROUTES.LOGIN), 1500);
      }
    } catch (error) {
      logger.error('Registration failed', error);
      toast.error(error.message || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  const getStepStatus = (stepId) => {
    if (stepId === 1) return phoneVerified ? 'done' : currentStep === 1 ? 'active' : 'pending';
    if (stepId === 2) return emailVerified ? 'done' : currentStep === 2 ? 'active' : 'pending';
    if (stepId === 3) return currentStep === 3 ? 'active' : 'pending';
  };

  return (
    <>
      <style>{`
        .register-root {
          position: fixed;
          inset: 0;
          display: flex;
          overflow: hidden;
        }

        /* ── LEFT PANEL ── */
        .reg-left {
          width: 42%;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
          position: relative;
          padding: 40px 44px;
          background: var(--reg-left-bg, #1e2a4a);
        }
        .dark .reg-left { --reg-left-bg: #111827; }

        .reg-left-blob1 {
          position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none;
          width: 320px; height: 320px; top: -80px; left: -80px;
          background: radial-gradient(circle, rgba(239,68,68,0.18) 0%, transparent 70%);
        }
        .reg-left-blob2 {
          position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none;
          width: 280px; height: 280px; bottom: 40px; right: -60px;
          background: radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%);
        }
        .reg-left-grid {
          position: absolute; inset: 0; pointer-events: none; opacity: 0.04;
          background-image: linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px);
          background-size: 36px 36px;
        }

        /* ── RIGHT PANEL ── */
        .reg-right {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          background: #f8fafc;
        }
        .dark .reg-right { background: #0f172a; }

        .reg-right-inner {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 100%;
          max-width: 620px;
          margin: 0 auto;
          padding: 28px 20px;
        }

        /* ── RESPONSIVE: tablet (768–1023px) ── */
        @media (max-width: 1023px) {
          .register-root { flex-direction: column; position: static; min-height: 100vh; overflow: visible; }
          .reg-left { width: 100%; flex-shrink: unset; padding: 32px 28px; min-height: unset; }
          .reg-left-features { display: none !important; }
          .reg-left-testimonial { display: none !important; }
          .reg-left-hero p { display: none; }
          .reg-right { overflow: visible; }
          .reg-right-inner { max-width: 100%; padding: 32px 24px; }
        }

        /* ── RESPONSIVE: mobile (<640px) ── */
        @media (max-width: 639px) {
          .reg-left { padding: 24px 20px; }
          .reg-left h1 { font-size: 24px !important; }
          .reg-right-inner { padding: 24px 16px; }
          .step-otp-grid { grid-template-columns: 1fr !important; }
          .pass-grid { grid-template-columns: 1fr !important; }
          .role-grid { grid-template-columns: 1fr 1fr; }
        }

        .feature-item { display: flex; align-items: flex-start; gap: 14px; }
        .feature-icon {
          width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .feature-item:hover .feature-icon { background: rgba(239,68,68,0.2); }

        .testimonial-card {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 20px;
        }

        .trust-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
          border-radius: 100px; padding: 5px 14px; margin-bottom: 20px;
        }

        /* Step progress */
        .step-dot-done    { background: #22c55e; color: #fff; }
        .step-dot-active  { background: var(--secondary-500, #ef4444); color: #fff; }
        .step-dot-pending { background: #e5e7eb; color: #9ca3af; }
        .dark .step-dot-pending { background: #1e293b; color: #475569; }

        .step-line-done { background: #22c55e; }
        .step-line-idle { background: #e5e7eb; }
        .dark .step-line-idle { background: #1e293b; }
      `}</style>

      <div className="register-root">

        {/* ════════════════════════════════
            LEFT — Branding Panel
        ════════════════════════════════ */}
        <div className="reg-left">
          <div className="reg-left-blob1" />
          <div className="reg-left-blob2" />
          <div className="reg-left-grid" />

          {/* Logo */}
          <Link to="/" className="relative z-10 flex items-center gap-3 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer" style={{ zIndex: 50 }}>
            <div className="w-7 h-7 rounded-xl bg-secondary-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <Rocket size={14} className="text-white" />
            </div>
            <span className="text-3xl xl:text-4xl font-bold text-white tracking-tight"><FowBrand size="small" /></span>
          </Link>

          {/* Hero */}
          <div className="reg-left-hero relative z-10 my-6">
            <div className="trust-badge">
              <Star size={12} className="text-secondary-400 fill-secondary-400" />
              <span className="text-xs font-semibold text-white/70 tracking-wide">Trusted by 50,000+ learners</span>
            </div>
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-3" style={{ lineHeight: 1.2 }}>
              Your journey to{' '}
              <span className="text-secondary-400">success</span>{' '}
              starts here
            </h1>
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              Join thousands of students and mentors building their dream careers on <span className="text-secondary-400 font-semibold">F.O.W</span>.
            </p>
          </div>

          {/* Features */}
          <div className="reg-left-features relative z-10 space-y-4 flex-1">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="feature-item">
                <div className="feature-icon">
                  <Icon size={17} className="text-secondary-400" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">{title}</p>
                  <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          {/* <div className="reg-left-testimonial relative z-10 mt-6 flex-shrink-0">
            <div className="testimonial-card">
              <div className="flex gap-0.5 mb-2">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-secondary-400 fill-secondary-400" />)}
              </div>
              <p className="text-white/70 text-xs leading-relaxed italic mb-3">
                "F.O.W completely changed how I approached my job search. The mentors here are incredible."
              </p>
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-secondary-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">P</div>
                <div>
                  <p className="text-white text-xs font-semibold leading-tight">Priya Sharma</p>
                  <p className="text-white/40 text-xs">Software Engineer at Google</p>
                </div>
              </div>
            </div>
          </div> */}
        </div>

        {/* ════════════════════════════════
            RIGHT — Registration Form
        ════════════════════════════════ */}
        <div className="reg-right">
          <div className="reg-right-inner">

            {/* Mobile logo */}
            <Link to="/" className="flex items-center gap-2 mb-7 lg:hidden hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center flex-shrink-0">
                <Rocket size={15} className="text-white" />
              </div>
              <span className="text-base font-bold text-primary-700"><FowBrand size="small" /></span>
            </Link>

            {/* Heading */}
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-primary-700">Create Account</h2>
              <p className="text-primary-500 mt-1 text-sm">Complete all steps to get started</p>
            </div>

            {/* Step progress */}
            <div className="flex items-center justify-center mb-8">
              {STEPS.map((step, i) => {
                const status = getStepStatus(step.id);
                const StepIcon = step.icon;
                return (
                  <div key={step.id} className="flex items-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                        status === 'done' ? 'step-dot-done' : status === 'active' ? 'step-dot-active' : 'step-dot-pending'
                      }`}>
                        {status === 'done' ? <CheckCircle size={20} /> : <StepIcon size={18} />}
                      </div>
                      <span className={`text-xs font-semibold ${
                        status === 'done' ? 'text-green-600' :
                        status === 'active' ? 'text-secondary-500' : 'text-gray-400'
                      }`}>{step.label}</span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`w-14 h-0.5 mb-6 mx-2 rounded-full transition-all ${
                        (i === 0 && phoneVerified) || (i === 1 && emailVerified) ? 'step-line-done' : 'step-line-idle'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-7">

              {/* ── STEP 1 ── */}
              <div className={phoneVerified ? 'opacity-60' : ''}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold tracking-wider ${
                    phoneVerified
                      ? 'bg-green-100 text-green-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    <Phone size={11} /> STEP 1
                  </span>
                  <h3 className="text-sm font-bold text-primary-700">Phone Verification</h3>
                  {phoneVerified && (
                    <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-green-600">
                      <CheckCircle size={13} /> Verified
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  <div className={`grid gap-3 step-otp-grid ${phoneOtpSent && !phoneVerified ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <PhoneInput label="Phone Number" placeholder="Enter phone number" disabled={phoneVerified} error={errors.phone?.message} fullWidth {...register('phone')} />
                    {phoneOtpSent && !phoneVerified && (
                      <Input label="6-Digit OTP" type="text" placeholder="Enter OTP" maxLength={6} error={errors.phoneOtp?.message} fullWidth {...register('phoneOtp')} />
                    )}
                  </div>
                  {!phoneVerified && (
                    <div className="flex gap-2">
                      {!phoneOtpSent ? (
                        <Button type="button" onClick={handleSendPhoneOTP} loading={loading} variant="primary">Send OTP</Button>
                      ) : (
                        <>
                          <Button type="button" onClick={handleVerifyPhoneOTP} loading={loading} variant="primary">
                            Verify <ArrowRight size={14} className="ml-1" />
                          </Button>
                          <Button type="button" onClick={() => handleResendOTP('phone')} disabled={resendCooldown > 0} variant="outline">
                            <RefreshCw size={13} className="mr-1" />
                            {resendCooldown > 0 ? `${resendCooldown}s` : 'Resend'}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── STEP 2 ── */}
              {phoneVerified && (
                <div className={emailVerified ? 'opacity-60' : ''}>
                  <div className="border-t border-gray-200 mb-6" />
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold tracking-wider ${
                      emailVerified
                        ? 'bg-green-100 text-green-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      <Mail size={11} /> STEP 2
                    </span>
                    <h3 className="text-sm font-bold text-primary-700">Email Verification</h3>
                    {emailVerified && (
                      <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-green-600">
                        <CheckCircle size={13} /> Verified
                      </span>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input label="First Name" type="text" placeholder="Enter first name" disabled={emailVerified} error={errors.firstName?.message} fullWidth {...register('firstName')} />
                      <Input label="Last Name" type="text" placeholder="Enter last name" disabled={emailVerified} error={errors.lastName?.message} fullWidth {...register('lastName')} />
                    </div>
                    <div className={`grid gap-3 step-otp-grid ${emailOtpSent && !emailVerified ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <Input label="Email Address" type="email" placeholder="Enter your email" disabled={emailVerified} error={errors.email?.message} fullWidth {...register('email')} />
                      {emailOtpSent && !emailVerified && (
                        <Input label="6-Digit OTP" type="text" placeholder="Enter OTP" maxLength={6} error={errors.emailOtp?.message} fullWidth {...register('emailOtp')} />
                      )}
                    </div>
                    {!emailVerified && (
                      <div className="flex gap-2">
                        {!emailOtpSent ? (
                          <Button type="button" onClick={handleSendEmailOTP} loading={loading} variant="primary">Send OTP</Button>
                        ) : (
                          <>
                            <Button type="button" onClick={handleVerifyEmailOTP} loading={loading} variant="primary">
                              Verify <ArrowRight size={14} className="ml-1" />
                            </Button>
                            <Button type="button" onClick={() => handleResendOTP('email')} disabled={resendCooldown > 0} variant="outline">
                              <RefreshCw size={13} className="mr-1" />
                              {resendCooldown > 0 ? `${resendCooldown}s` : 'Resend'}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── STEP 3 ── */}
              {emailVerified && (
                <div>
                  <div className="border-t border-gray-200 mb-6" />
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold tracking-wider bg-secondary-100 text-secondary-700">
                      <Lock size={11} /> STEP 3
                    </span>
                    <h3 className="text-sm font-bold text-primary-700">Complete Registration</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 pass-grid">
                      <PasswordInput label="Password" placeholder="Min. 8 characters" error={errors.password?.message} fullWidth showStrength strengthValue={passwordValue} {...register('password')} />
                      <PasswordInput label="Confirm Password" placeholder="Repeat password" error={errors.confirmPassword?.message} fullWidth {...register('confirmPassword')} />
                    </div>
                    <div className="text-xs text-primary-500 bg-primary-50 p-3 rounded-lg">
                      <p className="font-semibold mb-1">Password must contain:</p>
                      <ul className="list-disc list-inside space-y-0.5 pl-2">
                        <li>At least 8 characters</li>
                        <li>One uppercase letter (A-Z)</li>
                        <li>One lowercase letter (a-z)</li>
                        <li>One number (0-9)</li>
                      </ul>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-primary-700 mb-2">Select Your Role</label>
                      <div className="grid grid-cols-2 gap-3 role-grid">
                        <label className="cursor-pointer">
                          <input type="radio" value={USER_ROLES.STUDENT} className="peer sr-only" {...register('role')} />
                          <div className="border-2 border-gray-200 rounded-xl p-4 text-center transition-all peer-checked:border-secondary-500 peer-checked:bg-secondary-50 hover:border-gray-300">
                            <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-2">
                              <Users size={20} className="text-secondary-600" />
                            </div>
                            <div className="font-bold text-primary-700 text-sm">Student</div>
                            <div className="text-xs text-primary-500 mt-0.5">Learn & grow</div>
                          </div>
                        </label>
                        <label className="cursor-pointer relative group">
                          <input type="radio" value={USER_ROLES.MENTOR} className="peer sr-only" {...register('role')} />
                          <div className="border-2 border-gray-200 rounded-xl p-4 text-center transition-all peer-checked:border-secondary-500 peer-checked:bg-secondary-50 hover:border-gray-300">
                            <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-2">
                              <UserPlus size={20} className="text-secondary-600" />
                            </div>
                            <div className="flex items-center justify-center gap-1">
                              <div className="font-bold text-primary-700 text-sm">Mentor</div>
                              <div className="relative">
                                <Info size={14} className="text-primary-400 hover:text-primary-600 cursor-help" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 text-white text-xs rounded-lg p-3 shadow-lg z-10" style={{ backgroundColor: '#1e2a4a' }}>
                                  <div className="font-semibold mb-1">📄 Document Requirements</div>
                                  <p className="leading-relaxed">After registration, you'll need to upload 3 documents:</p>
                                  <ul className="list-disc list-inside mt-1 space-y-0.5 pl-1">
                                    <li>Aadhar Card</li>
                                    <li>Experience Certificate</li>
                                    <li>Degree Certificate</li>
                                  </ul>
                                  <p className="mt-1.5 text-yellow-200">Please keep soft copies ready!</p>
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border border-transparent" style={{ borderTopColor: '#1e2a4a' }}></div>
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-primary-500 mt-0.5">Guide others</div>
                          </div>
                        </label>
                      </div>
                      {errors.role && <p className="text-sm text-red-500 mt-1.5">{errors.role.message}</p>}
                    </div>
                    <Button type="submit" variant="primary" fullWidth loading={submitting} className="py-3 text-sm font-semibold">
                      Complete Registration
                    </Button>
                  </div>
                </div>
              )}
            </form>

            {/* Footer */}
            <div className="mt-8 pt-5 border-t border-gray-100 text-center">
              <p className="text-sm text-primary-500">
                Already have an account?{' '}
                <Link to={ROUTES.LOGIN} className="text-secondary-500 font-semibold hover:underline">
                  Login Here
                </Link>
              </p>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default RegisterPage;